import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Mic, MicOff, MessageSquare, PhoneOff, Send, Check, Upload, Download, Copy, Loader2 } from 'lucide-react';
import { interviewPublicAPI } from '@/lib/interviewAPI';
import useVoiceInterview from './useVoiceInterview';

const C = { ink: '#1E2A33', red: '#A62B1F', cream: '#F3EFE7', paper: '#FBF8F2', line: '#E3DDD1', muted: '#5C6670' };
const display = { fontFamily: 'Fraunces, Georgia, serif' };

const Shell = ({ children, data }) => (
  <div className="min-h-screen" style={{ background: C.paper, color: C.ink, fontFamily: 'Manrope, Arial, sans-serif' }}>
    <header className="border-b" style={{ borderColor: C.line, background: '#fff' }}>
      <div className="max-w-2xl mx-auto px-4 py-3 flex items-center gap-3">
        <img src="/stagehead-os-mark.svg" alt="" className="w-8 h-8" />
        <div className="leading-tight">
          <div className="font-semibold" style={display}>Stagehead Interview</div>
          {data?.company && <div className="text-xs" style={{ color: C.muted }}>for {data.company}</div>}
        </div>
      </div>
    </header>
    <main className="max-w-2xl mx-auto px-4 py-6 md:py-10">{children}</main>
  </div>
);

const Card = ({ children, className = '' }) => (
  <div className={`rounded-xl border bg-white p-5 md:p-6 ${className}`} style={{ borderColor: C.line }}>{children}</div>
);

const Btn = ({ children, onClick, disabled, variant = 'primary', className = '', type = 'button' }) => {
  const styles = variant === 'primary'
    ? { background: C.red, color: '#fff', borderColor: C.red }
    : { background: '#fff', color: C.ink, borderColor: C.line };
  return (
    <button type={type} onClick={onClick} disabled={disabled} style={styles}
      className={`inline-flex items-center justify-center gap-2 rounded-lg border px-4 py-2.5 text-sm font-semibold transition-opacity disabled:opacity-50 ${className}`}>
      {children}
    </button>
  );
};

// ── Transcript bubbles ────────────────────────────────────────────────────────
const Bubbles = ({ items, name }) => {
  const end = useRef(null);
  useEffect(() => { end.current && end.current.scrollIntoView({ behavior: 'smooth', block: 'end' }); }, [items.length]);
  return (
    <div className="space-y-3 max-h-[46vh] overflow-y-auto pr-1">
      {items.map((t, i) => (
        <div key={i} className={`flex ${t.role === 'user' ? 'justify-end' : 'justify-start'}`}>
          <div className="max-w-[85%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed"
            style={t.role === 'user' ? { background: C.ink, color: '#fff' } : { background: C.cream, color: C.ink }}>
            <div className="text-[10px] uppercase tracking-wider opacity-60 mb-0.5">{t.role === 'user' ? name : 'Host (AI)'}</div>
            {t.text}
          </div>
        </div>
      ))}
      <div ref={end} />
    </div>
  );
};

// ── Interview stage ──────────────────────────────────────────────────────────
const InterviewStage = ({ token, data, onFinished }) => {
  const [mode, setMode] = useState(null); // null | voice | chat
  const [items, setItems] = useState(data.transcript || []);
  const [msg, setMsg] = useState('');
  const [sending, setSending] = useState(false);
  const [chatError, setChatError] = useState(null);
  const onText = useCallback((t) => setItems((x) => [...x, t]), []);
  const voice = useVoiceInterview(interviewPublicAPI.voiceUrl(token), { onText });

  const startChat = async () => {
    setMode('chat');
    if (!items.length) {
      setSending(true);
      try {
        const r = await interviewPublicAPI.chat(token, '');
        if (r.data.reply) setItems([{ role: 'assistant', text: r.data.reply }]);
      } catch (e) {
        setChatError('The interviewer is unavailable right now. Please try again shortly.');
      } finally { setSending(false); }
    }
  };

  const send = async (e) => {
    e && e.preventDefault();
    const text = msg.trim();
    if (!text || sending) return;
    setMsg('');
    setItems((x) => [...x, { role: 'user', text }]);
    setSending(true);
    setChatError(null);
    try {
      const r = await interviewPublicAPI.chat(token, text);
      if (r.data.reply) setItems((x) => [...x, { role: 'assistant', text: r.data.reply }]);
    } catch (err) {
      setChatError(err?.response?.data?.detail || 'Something went wrong. Your message was saved; try again.');
    } finally { setSending(false); }
  };

  const finish = async () => {
    if (mode === 'voice') voice.stop();
    await interviewPublicAPI.finish(token).catch(() => {});
    onFinished();
  };

  const statusLabel = {
    connecting: 'Connecting…', listening: 'Listening', thinking: 'Thinking…', speaking: 'Host is speaking',
    ended: 'Call ended', error: 'Something went wrong', idle: '',
  }[voice.status];

  if (!mode) {
    return (
      <Card>
        <h1 className="text-2xl md:text-3xl font-semibold mb-2" style={display}>Hi {data.first_name},</h1>
        <p className="mb-4" style={{ color: C.muted }}>
          {data.requested_by ? `${data.requested_by} set you up` : "You've been set up"} with an interview to build your {data.kit}.
          {data.research_ready ? " We've already done our homework on you," : ''} so this is a conversation, not a form.
        </p>
        <ul className="text-sm space-y-2 mb-6">
          <li className="flex gap-2"><Check className="w-4 h-4 mt-0.5 shrink-0" style={{ color: C.red }} />Your host is an AI. It's about 15 to 20 minutes.</li>
          <li className="flex gap-2"><Check className="w-4 h-4 mt-0.5 shrink-0" style={{ color: C.red }} />The call is recorded so the team can write from your own words. Your voice is never cloned.</li>
          <li className="flex gap-2"><Check className="w-4 h-4 mt-0.5 shrink-0" style={{ color: C.red }} />You can stop any time and come back to this link to pick up where you left off.</li>
          <li className="flex gap-2"><Check className="w-4 h-4 mt-0.5 shrink-0" style={{ color: C.red }} />Nothing goes out until it's been reviewed and you've agreed to how it's used.</li>
        </ul>
        {data.has_interview && <p className="text-sm mb-4 font-medium">Welcome back. You can continue where you left off.</p>}
        <div className="flex flex-col sm:flex-row gap-3">
          {data.voice_available && (
            <Btn onClick={() => { setMode('voice'); voice.start(); }}><Mic className="w-4 h-4" />Start voice interview</Btn>
          )}
          <Btn variant="secondary" onClick={startChat}><MessageSquare className="w-4 h-4" />{data.voice_available ? 'Prefer to type? Chat instead' : 'Start the interview'}</Btn>
        </div>
        <p className="text-xs mt-4" style={{ color: C.muted }}>Use headphones if you can, and find a quiet spot.</p>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {mode === 'voice' && (
        <Card className="text-center">
          <div className="mx-auto mb-3 w-24 h-24 rounded-full flex items-center justify-center transition-transform"
            style={{ background: voice.status === 'speaking' ? C.red : C.ink, transform: `scale(${1 + Math.min(voice.level, 0.5) * 0.4})` }}>
            {voice.status === 'connecting' ? <Loader2 className="w-8 h-8 text-white animate-spin" /> : <Mic className="w-8 h-8 text-white" />}
          </div>
          <div className="font-semibold">{statusLabel}</div>
          {voice.error && <p className="text-sm mt-2" style={{ color: C.red }}>{voice.error}</p>}
          <div className="flex justify-center gap-3 mt-4 flex-wrap">
            {['ended', 'error'].includes(voice.status) ? (
              <Btn onClick={voice.start}><Mic className="w-4 h-4" />Reconnect</Btn>
            ) : (
              <>
                <Btn variant="secondary" onClick={voice.toggleMute}>{voice.muted ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}{voice.muted ? 'Unmute' : 'Mute'}</Btn>
                <Btn variant="secondary" onClick={voice.stop}><PhoneOff className="w-4 h-4" />Pause</Btn>
              </>
            )}
            <Btn variant="secondary" onClick={() => { voice.stop(); setMode('chat'); }}><MessageSquare className="w-4 h-4" />Switch to chat</Btn>
          </div>
        </Card>
      )}

      <Card>
        {items.length ? <Bubbles items={items} name={data.first_name} /> :
          <p className="text-sm" style={{ color: C.muted }}>{sending ? 'Your host is getting ready…' : 'The conversation will appear here.'}</p>}
        {mode === 'chat' && (
          <form onSubmit={send} className="mt-4 flex gap-2 items-end">
            <textarea value={msg} onChange={(e) => setMsg(e.target.value)} rows={2} placeholder="Type your answer…"
              onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) send(e); }}
              className="flex-1 rounded-lg border px-3 py-2 text-sm resize-none focus:outline-none" style={{ borderColor: C.line }} />
            <Btn type="submit" disabled={sending || !msg.trim()}>{sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}</Btn>
          </form>
        )}
        {chatError && <p className="text-sm mt-2" style={{ color: C.red }}>{chatError}</p>}
      </Card>

      {items.some((t) => t.role === 'user') && (
        <div className="text-center">
          <Btn variant="secondary" onClick={finish}><Check className="w-4 h-4" />I'm done for now</Btn>
        </div>
      )}
    </div>
  );
};

// ── Follow-ups ───────────────────────────────────────────────────────────────
const FollowUps = ({ token, data, onDone }) => {
  const [answers, setAnswers] = useState({});
  const [uploaded, setUploaded] = useState({});
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const open = data.followups.filter((f) => !f.answered);

  const upload = async (key, file) => {
    if (!file) return;
    setErr(null);
    try {
      await interviewPublicAPI.upload(token, key, file);
      setUploaded((u) => ({ ...u, [key]: file.name }));
    } catch (e) { setErr(e?.response?.data?.detail || 'Upload failed. Try a smaller file.'); }
  };

  const submit = async () => {
    setBusy(true);
    try { await interviewPublicAPI.answer(token, answers); onDone(); } catch (e) { setErr('Could not save. Please try again.'); } finally { setBusy(false); }
  };

  return (
    <Card>
      <h1 className="text-2xl font-semibold mb-1" style={display}>A few quick follow-ups</h1>
      <p className="text-sm mb-5" style={{ color: C.muted }}>Thanks for the interview, {data.first_name}. These round out your {data.kit}.</p>
      <div className="space-y-5">
        {open.map((f) => (
          <div key={f.key}>
            <label className="block text-sm font-semibold mb-1.5">{f.question}</label>
            {f.kind === 'photo' ? (
              <label className="inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-sm cursor-pointer" style={{ borderColor: C.line }}>
                <Upload className="w-4 h-4" />{uploaded[f.key] || 'Choose a file'}
                <input type="file" accept="image/*,.pdf" className="hidden" onChange={(e) => upload(f.key, e.target.files[0])} />
              </label>
            ) : (
              <input value={answers[f.key] || ''} onChange={(e) => setAnswers((a) => ({ ...a, [f.key]: e.target.value }))}
                placeholder={f.kind === 'link' ? 'https://' : ''}
                className="w-full rounded-lg border px-3 py-2 text-sm focus:outline-none" style={{ borderColor: C.line }} />
            )}
          </div>
        ))}
      </div>
      {err && <p className="text-sm mt-3" style={{ color: C.red }}>{err}</p>}
      <Btn onClick={submit} disabled={busy} className="mt-6">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}Send answers</Btn>
    </Card>
  );
};

// ── Consent ──────────────────────────────────────────────────────────────────
const Consent = ({ token, data, onDone }) => {
  const [name, setName] = useState('');
  const [agree, setAgree] = useState(false);
  const [guardian, setGuardian] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const submit = async () => {
    setBusy(true); setErr(null);
    try { await interviewPublicAPI.consent(token, { name, agree, guardian }); onDone(); }
    catch (e) { setErr(e?.response?.data?.detail || 'Could not save. Please try again.'); }
    finally { setBusy(false); }
  };
  return (
    <Card>
      <h1 className="text-2xl md:text-3xl font-semibold mb-2" style={display}>Your {data.kit} is ready</h1>
      <p className="text-sm mb-4" style={{ color: C.muted }}>Before you open it, please confirm how it can be used.</p>
      <ul className="space-y-2 text-sm mb-5 rounded-lg p-4" style={{ background: C.cream }}>
        {data.consent_terms.map((t, i) => <li key={i} className="flex gap-2"><Check className="w-4 h-4 mt-0.5 shrink-0" style={{ color: C.red }} />{t}</li>)}
      </ul>
      <label className="block text-sm font-semibold mb-1.5">Type your full name</label>
      <input value={name} onChange={(e) => setName(e.target.value)} className="w-full rounded-lg border px-3 py-2 text-sm mb-3" style={{ borderColor: C.line }} />
      <label className="block text-sm font-semibold mb-1.5">Parent or guardian name <span className="font-normal" style={{ color: C.muted }}>(only if you're under 18)</span></label>
      <input value={guardian} onChange={(e) => setGuardian(e.target.value)} className="w-full rounded-lg border px-3 py-2 text-sm mb-4" style={{ borderColor: C.line }} />
      <label className="flex gap-2 text-sm mb-5 cursor-pointer">
        <input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} className="mt-0.5" />
        I agree to the terms above.
      </label>
      {err && <p className="text-sm mb-3" style={{ color: C.red }}>{err}</p>}
      <Btn onClick={submit} disabled={busy || !agree || name.trim().length < 2}>{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}Agree and open</Btn>
    </Card>
  );
};

// ── Package view ─────────────────────────────────────────────────────────────
const Section = ({ title, children }) => (
  <div className="mb-6">
    <h2 className="text-xs font-bold uppercase tracking-widest mb-2" style={{ color: C.red }}>{title}</h2>
    {children}
  </div>
);

const CopyBtn = ({ text }) => {
  const [done, setDone] = useState(false);
  return (
    <button type="button" className="inline-flex items-center gap-1 text-xs font-semibold" style={{ color: C.muted }}
      onClick={() => { navigator.clipboard.writeText(text); setDone(true); setTimeout(() => setDone(false), 1500); }}>
      {done ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}{done ? 'Copied' : 'Copy'}
    </button>
  );
};

const PackageView = ({ token }) => {
  const [res, setRes] = useState(null);
  useEffect(() => { interviewPublicAPI.pkg(token).then((r) => setRes(r.data)).catch(() => setRes(false)); }, [token]);
  if (res === null) return <Card><Loader2 className="w-5 h-5 animate-spin" /></Card>;
  if (!res) return <Card>We couldn't load your package. Please refresh.</Card>;
  const p = res.package || {};
  const bios = p.bios || {};
  return (
    <Card>
      <div className="flex items-start justify-between gap-3 mb-6 flex-wrap">
        <div>
          <h1 className="text-3xl font-semibold" style={display}>{res.name}</h1>
          {bios.one_liner && <p className="mt-1" style={{ color: C.muted }}>{bios.one_liner}</p>}
        </div>
        <a href={interviewPublicAPI.pdfUrl(token)} className="inline-flex items-center gap-2 rounded-lg px-4 py-2.5 text-sm font-semibold text-white" style={{ background: C.red }}>
          <Download className="w-4 h-4" />Download PDF
        </a>
      </div>
      {['short', 'long'].filter((k) => bios[k]).map((k) => (
        <Section key={k} title={`${k === 'short' ? 'Short' : 'Long'} bio`}>
          <p className="text-sm leading-relaxed whitespace-pre-line">{bios[k]}</p>
          <CopyBtn text={bios[k]} />
        </Section>
      ))}
      {p.key_facts?.length > 0 && (
        <Section title="Key facts">
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1 text-sm">
            {p.key_facts.map((f, i) => (<div key={i} className="flex gap-2"><dt className="font-semibold">{f.label}:</dt><dd>{f.value}</dd></div>))}
          </dl>
        </Section>
      )}
      {p.current_project?.title && (
        <Section title="Current project"><p className="text-sm"><strong>{p.current_project.title}</strong>{p.current_project.date ? ` (${p.current_project.date})` : ''}</p><p className="text-sm">{p.current_project.description}</p></Section>
      )}
      {(p.talk_topics || p.services || []).length > 0 && (
        <Section title={p.talk_topics ? 'Talk topics' : 'What you do'}>
          {(p.talk_topics || p.services).map((t, i) => <p key={i} className="text-sm mb-2"><strong>{t.title}.</strong> {t.description}</p>)}
        </Section>
      )}
      {p.release_highlights?.length > 0 && (
        <Section title="Highlights">{p.release_highlights.map((r, i) => <p key={i} className="text-sm mb-1"><strong>{r.title}</strong> {r.note}</p>)}</Section>
      )}
      {p.quotes?.length > 0 && (
        <Section title="Quotes">{p.quotes.map((q, i) => <blockquote key={i} className="text-sm italic border-l-2 pl-3 mb-2" style={{ borderColor: C.red }}>“{q.text}”</blockquote>)}</Section>
      )}
      {p.press_angles?.length > 0 && (
        <Section title="Story angles">{p.press_angles.map((a, i) => <p key={i} className="text-sm mb-2"><strong>{a.headline}.</strong> {a.pitch}</p>)}</Section>
      )}
      {p.social_seeds?.length > 0 && (
        <Section title="Post ideas">
          <div className="space-y-3">
            {p.social_seeds.map((s, i) => (
              <div key={i} className="rounded-lg p-3 text-sm" style={{ background: C.cream }}>
                <div className="font-semibold">{s.idea} <span className="font-normal" style={{ color: C.muted }}>· {s.best_for}</span></div>
                <p className="mt-1 whitespace-pre-line">{s.caption}</p>
                <CopyBtn text={s.caption} />
              </div>
            ))}
          </div>
        </Section>
      )}
    </Card>
  );
};

// ── Page ─────────────────────────────────────────────────────────────────────
export default function InterviewPublic() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [missing, setMissing] = useState(false);
  const load = useCallback(() => interviewPublicAPI.get(token).then((r) => setData(r.data)).catch(() => setMissing(true)), [token]);
  useEffect(() => { load(); }, [load]);

  if (missing) return <Shell><Card>This interview link isn't valid. Check the link in your text or email.</Card></Shell>;
  if (!data) return <Shell><div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin" /></div></Shell>;

  let body;
  switch (data.stage) {
    case 'follow_up': body = <FollowUps token={token} data={data} onDone={load} />; break;
    case 'done':
      body = (
        <Card>
          <h1 className="text-2xl font-semibold mb-2" style={display}>Thanks, {data.first_name}!</h1>
          <p className="text-sm" style={{ color: C.muted }}>That's everything we need for now. The team is putting your {data.kit} together from your own words. We'll text or email you if anything else comes up, and again when it's ready.</p>
          <Btn variant="secondary" className="mt-5" onClick={() => setData({ ...data, stage: 'interview', has_interview: true })}>Add something to the interview</Btn>
        </Card>
      ); break;
    case 'in_review':
      body = <Card><h1 className="text-2xl font-semibold mb-2" style={display}>Your {data.kit} is being reviewed</h1><p className="text-sm" style={{ color: C.muted }}>The team is reviewing the draft. You'll get a text or email as soon as it's approved.</p></Card>;
      break;
    case 'ready': body = <Consent token={token} data={data} onDone={load} />; break;
    case 'delivered': body = <PackageView token={token} />; break;
    default: body = <InterviewStage token={token} data={data} onFinished={load} />;
  }
  return <Shell data={data}>{body}</Shell>;
}
